import { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import { NotesInput } from 'components/Ponder/styles';
import Button from 'components/Button';
import Text from 'components/core/Text';
import { SessionWorkout } from 'reducers/workout/types';
import { RATINGS } from 'constants/ponder';
import { RatingCards } from 'components/Ponder/RatingQuestion';
import { requestUpdateWorkoutSurvey } from 'actions/workout';
import { hideModal } from 'actions/modal';
import { StoreType } from 'reducers/types';
import { Content, Background as Overlay } from '../styles';
import { CloseIcon, InputWrapper, ModalContainer, RatingGroup, ModalControls } from './styles';

interface Props {
  modalData: {
    workout: SessionWorkout;
  };
}

const PonderEditing: FC<Props> = ({ modalData }) => {
  const { workout } = modalData;
  const isUpdating = useSelector(
    (state: StoreType) => state.workout.sessions.items.find((w) => w.id === workout.id)?.isUpdating,
  );
  const dispatch = useDispatch();
  const survey = workout?.workoutSurvey ?? {};

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(hideModal());
    },
    [dispatch],
  );

  const onSubmit = useCallback(
    (values) => {
      dispatch(
        requestUpdateWorkoutSurvey({
          ...workout,
          workoutSurvey: { ...workout.workoutSurvey, ...values },
        }),
      );
    },
    [dispatch, workout],
  );

  return (
    <>
      <Overlay onClick={handleClose} />
      <Content>
        <CloseIcon onClick={handleClose} />
        <ModalContainer>
          <Formik
            initialValues={{
              ponder: survey.ponder,
              quality: survey.quality,
              ease: survey.ease,
              density: survey.density,
            }}
            onSubmit={onSubmit}
            render={({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
              <>
                <Text variant="displayX" marginBottom={6}>
                  Ponder
                </Text>
                <InputWrapper>
                  <NotesInput
                    name="ponder"
                    value={values.ponder}
                    placeholder="Anything worth remembering."
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </InputWrapper>
                {RATINGS.map((rating) => (
                  <RatingGroup key={rating.type}>
                    <Text variant="displayM" marginBottom={4}>
                      {rating.title}
                    </Text>
                    <RatingCards
                      rating={rating}
                      displayOrder={
                        survey.displayOrder?.[rating.type] ??
                        rating.options.map((option) => option.value)
                      }
                      value={values[rating.type]}
                      onSelect={(value) => setFieldValue(rating.type, value)}
                    />
                  </RatingGroup>
                ))}
                <ModalControls>
                  <Button handleClick={handleSubmit} isLoading={isUpdating}>
                    Save
                  </Button>
                  <Button handleClick={handleClose} secondary>
                    Cancel
                  </Button>
                </ModalControls>
              </>
            )}
          />
        </ModalContainer>
      </Content>
    </>
  );
};

export default PonderEditing;
